/**
 * Tabs Component
 * 
 * A reusable tab component for switching between different views (e.g., "My Matters" / "Team's Matters")
 * 
 * Design Principles:
 * - Clean tab interface with active state indication
 * - Smooth transitions between tabs
 * - Accessible keyboard navigation
 * - Consistent spacing and padding
 */

import React from 'react';
import './Tabs.css';

export interface Tab {
  /** Unique identifier for the tab */
  id: string;
  /** Display label for the tab */
  label: string;
  /** Optional count to display next to label (e.g., "24 of 142") */
  count?: string | number;
  /** Disabled state */
  disabled?: boolean;
}

export interface TabsProps {
  /** Array of tab definitions */
  tabs: Tab[];
  /** Currently active tab ID */
  activeTabId: string;
  /** Callback when tab is clicked */
  onTabChange: (tabId: string) => void;
  /** Additional CSS class name */
  className?: string;
  /** Variant style */
  variant?: 'default' | 'underline' | 'pills';
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  className = '',
  variant = 'default',
}) => {
  const handleTabClick = (tabId: string, disabled?: boolean) => {
    if (!disabled) {
      onTabChange(tabId);
    }
  };

  /** Move selection (and focus) to another tab by offset, skipping disabled ones. */
  const moveFocus = (e: React.KeyboardEvent, from: number, step: number): void => {
    const enabled = tabs.map((t, i) => ({ t, i })).filter(({ t }) => !t.disabled);
    if (!enabled.length) return;
    const at = enabled.findIndex(({ i }) => i === from);
    const next = enabled[(((at === -1 ? 0 : at) + step) % enabled.length + enabled.length) % enabled.length];
    onTabChange(next.t.id);
    // Roving tabindex means the newly-selected tab becomes the only tab stop, so focus has to
    // follow it or the keyboard user is left focused on an element with tabIndex -1.
    const strip = (e.currentTarget as HTMLElement).parentElement;
    const buttons = strip ? Array.from(strip.querySelectorAll<HTMLElement>('[role="tab"]')) : [];
    buttons[next.i]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, tabId: string, disabled?: boolean, index = 0) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onTabChange(tabId);
      }
      return;
    }
    // Arrow/Home/End navigation. `tabIndex` already implements the roving-tabindex half of the ARIA
    // tabs pattern (only the active tab is a tab stop) — without these keys the other half was
    // missing, so every tab but the active one was unreachable by keyboard entirely.
    const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (e.key in keys) {
      e.preventDefault();
      moveFocus(e, index, keys[e.key]);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const enabled = tabs.filter((t) => !t.disabled);
      const target = e.key === 'Home' ? enabled[0] : enabled[enabled.length - 1];
      if (target) moveFocus(e, tabs.indexOf(target), 0);
    }
  };

  return (
    <div className={`tabs-container tabs-${variant} ${className}`} role="tablist">
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;
        return (
          <button
            key={tab.id}
            className={`tab-button ${isActive ? 'tab-active' : ''} ${tab.disabled ? 'tab-disabled' : ''}`}
            onClick={() => handleTabClick(tab.id, tab.disabled)}
            onKeyDown={(e) => handleKeyDown(e, tab.id, tab.disabled, index)}
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
            tabIndex={tab.disabled ? -1 : isActive ? 0 : -1}
            disabled={tab.disabled}
          >
            <span className="tab-label">{tab.label}</span>
            {tab.count !== undefined && (
              <span className="tab-count">{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;

